import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react'

import {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from '@aeris/ui'

import { DESKTOP_SHORTCUTS } from '../../lib/desktop/desktop-shortcuts.ts'
import { menuSections } from '../../lib/desktop/menu-sections.ts'
import {
  useDesktopSelector,
  windowLabel,
  type DesktopStore,
  type DesktopWindow,
} from '../../lib/desktop/window-store.ts'
import {
  MODULE_GROUPS,
  type ModuleEntry,
  type ModuleGroup,
} from '../../lib/modules.tsx'
import { useVisibleModules } from '../../lib/use-visible-modules.ts'
import { useWorkspace } from '../../lib/workspace.ts'

import { AboutDialog } from './about-dialog.tsx'

/**
 * A barra de menus do Modo PC — F138.
 *
 * Um menu por grupo do catálogo, na ordem de um dia de trabalho, e depois
 * **Janela** (arrumar e alternar as janelas abertas) e **Ajuda** (os atalhos).
 * É o índice completo do produto, o que no Modo Site é a barra lateral; a barra
 * de ícones logo abaixo é o acesso rápido.
 *
 * As telas vêm da mesma regra da barra lateral e da busca
 * (`useVisibleModules`): o que a pessoa não pode abrir não aparece.
 */

const GROUP_TITLES: Record<ModuleGroup, string> = {
  operação: 'Operação',
  atendimento: 'Atendimento',
  catálogo: 'Catálogo',
  gestão: 'Gestão',
  administração: 'Administração',
}

export const DesktopMenubar = ({
  store,
}: {
  store: DesktopStore
}): ReactNode => {
  const visible = useVisibleModules({ includeTabs: true })
  const sections = useMemo(() => menuSections(visible), [visible])
  const workspace = useWorkspace()
  const [about, setAbout] = useState(false)

  const windows = useDesktopSelector(store, (state) => state.windows)
  const mru = useDesktopSelector(store, (state) => state.mru)
  const activeId = useDesktopSelector(store, (state) => state.activeId)

  /*
   * Shift+clique (ou Shift+Enter) abre outra janela da tela. O `onSelect` do
   * Radix não traz o evento do mouse nem do teclado, então o Shift é lembrado
   * no instante em que o item é apertado.
   */
  const shift = useRef(false)
  const remember = (event: { shiftKey: boolean }): void => {
    shift.current = event.shiftKey
  }

  const screenItem = (entry: ModuleEntry, nested: boolean): ReactNode => (
    <MenubarItem
      key={entry.module}
      onPointerDown={remember}
      onKeyDown={remember}
      onSelect={() => {
        workspace.open({ module: entry.module, newInstance: shift.current })
        shift.current = false
      }}
      className={nested ? 'pl-8' : undefined}
    >
      <entry.Icon
        aria-hidden="true"
        className="h-4 w-4 shrink-0 text-(--text-secondary)"
      />
      {nested ? (entry.tabLabel ?? entry.label) : entry.label}
    </MenubarItem>
  )

  // As abertas, da usada por último para a mais antiga.
  const recent = mru
    .map((id) => windows.find((entry) => entry.id === id))
    .filter((entry): entry is DesktopWindow => entry !== undefined)

  const none = windows.length === 0

  return (
    <Menubar
      aria-label="Menu"
      className="shrink-0 border-b border-(--border-default) bg-(--surface)"
    >
      {MODULE_GROUPS.map((group) => {
        const entries = sections.get(group) ?? []
        if (entries.length === 0) return null

        return (
          <MenubarMenu key={group}>
            <MenubarTrigger>{GROUP_TITLES[group]}</MenubarTrigger>
            <MenubarContent>
              {entries.map(({ entry, children }) => (
                <Fragment key={entry.module}>
                  {screenItem(entry, false)}
                  {children.map((child) => screenItem(child, true))}
                </Fragment>
              ))}
            </MenubarContent>
          </MenubarMenu>
        )
      })}

      <MenubarMenu>
        <MenubarTrigger>Janela</MenubarTrigger>
        <MenubarContent>
          <MenubarItem
            disabled={none}
            onSelect={() => {
              store.dispatch({ type: 'cascade' })
            }}
          >
            Cascata
          </MenubarItem>
          <MenubarItem
            disabled={none}
            onSelect={() => {
              store.dispatch({ type: 'tile' })
            }}
          >
            Lado a lado
          </MenubarItem>
          <MenubarItem
            disabled={none}
            onSelect={() => {
              store.dispatch({ type: 'minimizeAll' })
            }}
          >
            Minimizar todas
          </MenubarItem>
          <MenubarItem
            disabled={none}
            onSelect={() => {
              store.dispatch({ type: 'closeAll' })
            }}
          >
            Fechar todas
          </MenubarItem>

          {recent.length === 0 ? null : (
            <>
              <MenubarSeparator />
              <MenubarLabel>Abertas</MenubarLabel>
              {recent.map((entry) => (
                <MenubarCheckboxItem
                  key={entry.id}
                  checked={entry.id === activeId}
                  onSelect={() => {
                    store.dispatch({ type: 'focus', id: entry.id })
                  }}
                >
                  <span className="truncate">{windowLabel(entry)}</span>
                  {entry.mode === 'minimized' ? (
                    <span className="ml-auto pl-3 text-(length:--text-xs) text-(--text-muted)">
                      minimizada
                    </span>
                  ) : null}
                </MenubarCheckboxItem>
              ))}
            </>
          )}
        </MenubarContent>
      </MenubarMenu>

      <MenubarMenu>
        <MenubarTrigger>Ajuda</MenubarTrigger>
        <MenubarContent className="w-80">
          <MenubarLabel>Atalhos da área de trabalho</MenubarLabel>
          <dl className="flex flex-col px-2 pb-1">
            {DESKTOP_SHORTCUTS.map(({ keys, does }) => (
              <div
                key={keys}
                className="flex items-center justify-between gap-4 py-1 text-(length:--text-sm)"
              >
                <dt className="text-(--text-primary)">{does}</dt>
                <dd>
                  <kbd className="rounded-(--radius-sm) border border-(--border-subtle) px-1.5 font-(--font-mono) text-(length:--text-xs) text-(--text-secondary)">
                    {keys}
                  </kbd>
                </dd>
              </div>
            ))}
          </dl>

          <MenubarSeparator />
          {/* O glossário: o que cada situação e cada cor querem dizer (F156). */}
          <MenubarItem
            onSelect={() => {
              workspace.open({ module: 'ajuda' })
            }}
          >
            Situações e cores
          </MenubarItem>
          {/*
            O ÚNICO caminho até a configuração da máquina (F155).

            A tela `/servidor` existe desde a F040, e o aplicativo instalado não
            tem barra de endereço — então, na prática, trocar o servidor de uma
            loja exigia editar `settings.json` à mão. "Sobre" leva lá, e de
            quebra responde as perguntas que o suporte faz por telefone.
          */}
          <MenubarItem
            onSelect={() => {
              setAbout(true)
            }}
          >
            Sobre o Aeris One
          </MenubarItem>
        </MenubarContent>
      </MenubarMenu>

      {about ? (
        <AboutDialog
          onClose={() => {
            setAbout(false)
          }}
          onOpenServer={() => {
            /*
             * Recarregar, e não navegar pelo roteador: `/servidor` vive fora do
             * shell de propósito (quem troca de servidor pode estar apontando
             * para um que não responde), e o menu que abriu este diálogo é
             * parte do shell.
             */
            window.location.assign('/servidor')
          }}
        />
      ) : null}
    </Menubar>
  )
}
